import type { SettingsChange } from '@aithora/shared';
import { hhmm } from './kit';

/** The design's history row (seHist, L21858): when, who, what, a tag. */
export interface HistRow {
  at: string;
  who: string;
  what: string;
  tag: string;
}

const ROLE_WORD: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin', OPERATOR: 'Operator', OWNER: 'Operator', ADMIN_TOKEN: 'Operator', PARTNER: 'Partner', SALES: 'Sales', DEVELOPER: 'Dev',
  ADMIN: 'Admin', CAREGIVER: 'Care Staff', STAFF: 'Care Staff', FAMILY_MEMBER: 'Family', RESIDENT: 'Resident', MEMBER: 'Member', SYSTEM: 'System',
};

/** "Keisha Ruiz, RN" -> "K. Ruiz"; a single word stays as it is. */
export function shortName(name: string | null): string {
  const n = String(name ?? '').split(',')[0].trim();
  if (!n) return 'Someone';
  const w = n.split(/\s+/);
  return w.length < 2 ? n : `${w[0][0]}. ${w[w.length - 1]}`;
}

/** "Today · 14:02" or "Sep 24 · 14:02" (the design's stamp and seed rows). */
export function histAt(iso: string, now = Date.now()): string {
  const d = new Date(iso);
  const n = new Date(now);
  const same = d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
  const day = same ? 'Today' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${day} · ${hhmm(d)}`;
}

const FIELD_LABEL: Record<string, string> = {
  region: 'Region', timezone: 'Time zone', licensed_beds: 'Licensed beds', ownership: 'Ownership', units: 'Units', baa_status: 'BAA status',
  legalName: 'Legal name', street: 'Street', city: 'City', state: 'State', zip: 'ZIP', npi: 'NPI', ccn: 'CCN', facilityId: 'Linked facility id',
  plan: 'Plan / tier', billingContactName: 'Billing contact', billingContactEmail: 'Billing email', baaStatus: 'BAA status', baaSignedDate: 'Signed date',
  role: 'Role', alert_phone: 'Alert phone', retention: 'Ledger & audit retention', exportCadence: 'Export cadence', gate: 'Gate',
};

/** Fields whose change reads as a sentence of its own. */
const SENTENCE: Record<string, string> = {
  quiet_hours: 'Quiet hours changed', default_posture: 'Default posture changed', passkey_added: 'Added a passkey', passkey_deleted: 'Deleted a passkey',
  backup_codes: 'Regenerated backup codes', api_key_created: 'Created an API key', api_key_revoked: 'Revoked an API key', rotate_all_keys: 'Rotated every key',
  purge_sample: 'Purged sample data', organization_deletion: 'Organization deletion', device_removed: 'Removed a device', device_name: 'Renamed a device',
  multi_sig: 'Changed signature requirement', twofa_disabled: 'Turned off two-factor', live_key: 'Rotated the live key',
};

const label = (f: string) => FIELD_LABEL[f] ?? f.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
const scalar = (v: unknown) => v === null || v === undefined || typeof v !== 'object';
const show = (v: unknown) => (v === null || v === undefined || v === '' ? 'Not set' : String(v));

/** What a change did, in a line (the design's "what"). */
export function describe(c: SettingsChange): string {
  const f = c.field ?? '';
  if (SENTENCE[f]) {
    if (f === 'organization_deletion') {
      const s = (c.after as { status?: string } | null)?.status;
      return s === 'HOLD' ? 'Organization deletion signed · 30-day hold started' : s === 'CANCELLED' ? 'Organization deletion cancelled' : s === 'DONE' ? 'Organization deleted' : 'Started organization deletion · awaiting second signature';
    }
    return SENTENCE[f];
  }
  if (!f) return 'Changed';
  if (scalar(c.before) && scalar(c.after)) return `${label(f)}: ${show(c.before)} → ${show(c.after)}`;
  if (c.before && c.after && typeof c.before === 'object' && typeof c.after === 'object' && !Array.isArray(c.before)) {
    const b = c.before as Record<string, unknown>;
    const a = c.after as Record<string, unknown>;
    const keys = Object.keys({ ...b, ...a }).filter((k) => scalar(b[k]) && scalar(a[k]) && show(b[k]) !== show(a[k]));
    if (keys.length && keys.length <= 3 && !('userId' in a)) return keys.map((k) => `${label(k)}: ${show(b[k])} → ${show(a[k])}`).join(' · ');
  }
  return `${label(f)} changed`;
}

/** One server row as the design's history row. */
export function histRow(c: SettingsChange, meId: string | null, now = Date.now()): HistRow {
  const role = ROLE_WORD[String(c.actorRole ?? '')] ?? (c.actorRole ? String(c.actorRole) : '');
  const mine = !!meId && c.actorId === meId;
  const name = mine ? 'You' : c.actorRole === 'ADMIN_TOKEN' ? 'Aithora' : shortName(c.actorName);
  return {
    at: histAt(c.at, now),
    who: role ? `${name} · ${role}` : name,
    what: describe(c) + (c.reason === 'undo' ? ' (undo)' : ''),
    tag: '',
  };
}
